import { api } from '../../core/network/api';
import type { ItemCopy } from '../config/item-copy';

/**
 * design/gdd/equipment.md — the paperdoll's two live slots.
 *
 * The catalog is served, not mirrored: every number on it is read off
 * `WEAPONS`/`BASE_WEIGHT_KG` at request time, so a combat rebalance reaches
 * the picker with no edit here. Same lesson `game-modes.controller.ts` cites
 * `ITEM_DEFS` for.
 */

export type EquipmentSlot = 'WEAPON' | 'OFFHAND';

export interface EquipmentCatalogRow {
  id: string;
  slot: EquipmentSlot;
  name: string;
  /** Filename under `assets/ui/gear/`, and under the pack's `equipment/`. */
  model: string;
  weaponClass?: 'LIGHT' | 'MEDIUM' | 'BLUNT' | 'HEAVY' | 'REACH';
  hands?: 1 | 2;
  blurb: string;
  /** The long form, shown when a piece is opened. Optional for the same back-compat reason as the satchel's. */
  copy?: ItemCopy;
  damage?: number;
  swingMs?: number;
  reachM?: number;
  staminaCost?: number;
  totalWeightKg: number;
  /**
   * What it costs to unlock, in the coin `currency` names. Absent means it
   * comes with the account. The field kept its Wax name when the Chandlery
   * arms arrived (2026-09-07) because every caller reads it positionally and
   * only the glyph beside it changes — the same call `GearCatalogRow` made.
   */
  priceWax?: number;
  /** False only for a paid piece this account has not bought yet. */
  owned: boolean;
  /** What the vendor pays to take it back. Absent on a free piece, which cannot be sold. */
  buybackWax?: number;
  /** Which wallet the two numbers above are quoted in. Absent on a free piece. */
  currency?: 'SOFT' | 'PREMIUM';
}

/** What a Sell call gives back. `unequippedFrom` names any delver that just put the piece down. */
export interface BuybackResult {
  defId: string;
  price: number;
  softBalance: number;
  ownedIds: string[];
  unequippedFrom: string[];
}

export interface EquippedArms {
  weapon: EquipmentCatalogRow;
  offhand: EquipmentCatalogRow | null;
  weaponClass: 'LIGHT' | 'MEDIUM' | 'BLUNT' | 'HEAVY' | 'REACH';
  weightKg: number;
}

export const getEquipmentCatalog = (): Promise<EquipmentCatalogRow[]> => api.get('/equipment/catalog');

export const getEquipment = (characterId: string): Promise<EquippedArms> =>
  api.get(`/equipment/${encodeURIComponent(characterId)}`);

/** `defId: null` empties the slot; only the off-hand may actually be empty. */
export const equipItem = (characterId: string, slot: EquipmentSlot, defId: string | null): Promise<EquippedArms> =>
  api.post(`/equipment/${encodeURIComponent(characterId)}`, { slot, defId });

/** Unlocks a paid piece for the whole account. Wax only — see the backend def table on why. */
export const purchaseArms = (defId: string): Promise<{ softBalance: number; ownedIds: string[] }> =>
  api.post('/equipment/purchase', { defId });

/**
 * Sell a paid piece back for Wax, minus the vendor's commission.
 *
 * Irreversible in the ordinary sense — you can buy it again at full price —
 * so the caller confirms first. The response says which delvers were holding
 * it, because selling something out from under a character you were not
 * looking at is a surprise worth naming.
 */
export const sellArms = (defId: string): Promise<BuybackResult> =>
  api.post('/equipment/sell', { defId });
